(() => {
  const selector = '.button, .home-button, .for-her-promo__button, .search-input button, .brands-filter-row button';
  const motion = matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
  const pills = new Map();
  let pointer = null;
  let pressed = false;
  let frame = 0;
  let lastTime = 0;
  let sizesDirty = true;

  const invalidate = () => {
    sizesDirty = true;
    wake();
  };
  const resize = new ResizeObserver(invalidate);
  const disabled = (element) => element.matches(':disabled, [aria-disabled="true"], [aria-busy="true"]') || element.closest('[inert]');

  function scan() {
    for (const [element] of pills) {
      if (!element.isConnected) {
        resize.unobserve(element);
        pills.delete(element);
      }
    }
    if (!motion.matches) return;
    document.querySelectorAll(selector).forEach((element) => {
      if (pills.has(element) || element.closest('.magnetic-pill-visual')) return;
      const visual = document.createElement('span');
      visual.className = 'magnetic-pill-visual';
      visual.append(...element.childNodes);
      element.append(visual);
      element.classList.add('magnetic-pill-ready');
      pills.set(element, { visual, x: 0, y: 0, vx: 0, vy: 0 });
      resize.observe(element);
    });
    invalidate();
  }

  function reset() {
    pointer = null;
    for (const [element, state] of pills) {
      state.x = state.y = state.vx = state.vy = 0;
      element.style.removeProperty('--pill-x');
      element.style.removeProperty('--pill-y');
    }
    cancelAnimationFrame(frame);
    frame = lastTime = 0;
  }

  function wake() {
    if (!frame && pills.size && !document.hidden) frame = requestAnimationFrame(tick);
  }

  function tick(time) {
    frame = 0;
    const dt = Math.min(lastTime ? (time - lastTime) / 1000 : 1 / 60, 1 / 30);
    lastTime = time;
    let nearest = null;
    let nearestDistance = Infinity;
    const rects = new Map();
    const measurements = [];

    for (const [element, state] of pills) {
      if (sizesDirty) {
        const css = getComputedStyle(element);
        measurements.push([element, ['Top', 'Right', 'Bottom', 'Left'].map((side) =>
          -(parseFloat(css['padding' + side]) + parseFloat(css['border' + side + 'Width'])))]);
      }
      if (!motion.matches || !pointer || pressed || disabled(element) || element.matches(':focus-visible')) continue;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.bottom < 0 || rect.top > innerHeight) continue;
      rects.set(element, rect);
      const edgeX = Math.max(rect.left - pointer.x, 0, pointer.x - rect.right);
      const edgeY = Math.max(rect.top - pointer.y, 0, pointer.y - rect.bottom);
      const distance = Math.hypot(edgeX, edgeY);
      const interactive = pointer.target?.closest('a, button, input, select, textarea, summary, label');
      if (interactive && interactive !== element && !interactive.contains(element) && !element.contains(interactive)) continue;
      const rank = distance + Math.hypot(pointer.x - rect.x - rect.width / 2, pointer.y - rect.y - rect.height / 2) * 0.001;
      if (distance < 56 && rank < nearestDistance) {
        nearest = element;
        nearestDistance = rank;
      }
    }

    for (const [element, insets] of measurements) {
      ['top', 'right', 'bottom', 'left'].forEach((side, index) => element.style.setProperty('--pill-inset-' + side, insets[index] + 'px'));
    }
    sizesDirty = false;
    let moving = false;
    for (const [element, state] of pills) {
      let targetX = 0;
      let targetY = 0;
      if (element === nearest) {
        const rect = rects.get(element);
        const dx = pointer.x - rect.x - rect.width / 2;
        const dy = pointer.y - rect.y - rect.height / 2;
        const distance = Math.hypot(Math.max(Math.abs(dx) - rect.width / 2, 0), Math.max(Math.abs(dy) - rect.height / 2, 0));
        const strength = 0.25 * (1 - distance / 56);
        targetX = Math.max(-26, Math.min(26, dx * strength));
        targetY = Math.max(-14, Math.min(14, dy * strength));
        targetX = Math.max(4 - rect.left, Math.min(innerWidth - 4 - rect.right, targetX));
      }
      if (pressed) continue;
      if (disabled(element) || element.matches(':focus-visible')) {
        state.x = state.y = state.vx = state.vy = 0;
      } else {
        const step = dt / 2;
        for (let i = 0; i < 2; i++) {
          state.vx += ((targetX - state.x) * 320 - state.vx * 28) * step;
          state.vy += ((targetY - state.y) * 320 - state.vy * 28) * step;
          state.x += state.vx * step;
          state.y += state.vy * step;
        }
      }
      const settled = Math.abs(state.x - targetX) + Math.abs(state.y - targetY) + Math.abs(state.vx) + Math.abs(state.vy) < 0.06;
      if (settled) {
        state.x = targetX;
        state.y = targetY;
        state.vx = state.vy = 0;
      } else moving = true;
      element.style.setProperty('--pill-x', state.x.toFixed(3) + 'px');
      element.style.setProperty('--pill-y', state.y.toFixed(3) + 'px');
    }
    if (moving) wake();
    else lastTime = 0;
  }

  document.addEventListener('pointermove', (event) => {
    if (!motion.matches || event.pointerType !== 'mouse') return;
    pointer = { x: event.clientX, y: event.clientY, target: event.target };
    wake();
  }, { passive: true });
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse') reset();
    pressed = true;
  }, { passive: true });
  const release = () => { pressed = false; pointer = null; wake(); };
  document.addEventListener('pointerup', release, { passive: true });
  document.addEventListener('pointercancel', release, { passive: true });
  document.documentElement.addEventListener('pointerleave', release, { passive: true });
  document.addEventListener('keydown', reset);
  document.addEventListener('focusin', () => {
    if (document.activeElement?.matches(':focus-visible')) reset();
  });
  window.addEventListener('blur', () => { pressed = false; reset(); });
  window.addEventListener('scroll', () => { pointer = null; wake(); }, { passive: true, capture: true });
  window.addEventListener('resize', () => { pointer = null; invalidate(); }, { passive: true });
  document.addEventListener('visibilitychange', reset);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', scan);
  motion.addEventListener('change', () => { reset(); scan(); });
  new MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList')) scan();
    else wake();
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy'] });
  scan();
})();
